import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import NavBar from '../NavBar/NavBar';
import { appConfig } from '../../config/appConfig';
import './Perfil.css';

function Perfil() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [otherUsers, setOtherUsers] = useState([]);
  const [userDepartment, setUserDepartment] = useState('');
  const storage = getStorage();

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    telefono: '',
    direccion: '',
    departamento: '',
    photoURL: '',
    status_cliente: '1'
  });

  useEffect(() => {
    const cargarPerfil = async () => {
      try {
        setLoading(true);
        // Get current user data
        const userRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
        const userDocSnap = await getDoc(userRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setFormData({
            displayName: userData.nombre_cliente || auth.currentUser.displayName || '',
            email: auth.currentUser.email,
            telefono: userData.telefono_cliente || '',
            direccion: userData.direccion_cliente || '',
            departamento: userData.departamento || '',
            photoURL: userData.photo_url || auth.currentUser.photoURL || '',
            status_cliente: userData.status_cliente || '1'
          });
          
          // Guardar el departamento del usuario actual
          setUserDepartment(userData.departamento || '');
          
          // Instead of using a compound query that requires an index,
          // we'll get all users and filter in the client
          const usuariosRef = collection(db, 'levapan_usuarios');
          const usuariosSnapshot = await getDocs(usuariosRef);
          
          // Filter the documents in the client side
          // This avoids the need for the composite index
          const companerosData = usuariosSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(doc => 
              // Keep only users from same department and exclude current user
              doc.departamento === userData.departamento && doc.id !== auth.currentUser.uid
            );
          
          setOtherUsers(companerosData);
        } else {
          // Si no existe el documento, usamos los datos del auth
          setFormData({
            displayName: auth.currentUser.displayName || '',
            email: auth.currentUser.email,
            telefono: '',
            direccion: '',
            departamento: '',
            photoURL: auth.currentUser.photoURL || '',
            status_cliente: '1'
          });
        }
      } catch (error) {
        console.error("Error al cargar el perfil:", error);
        setError("Error al cargar el perfil. Intenta nuevamente.");
      } finally {
        setLoading(false);
      }
    };

    cargarPerfil();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      let photoURL = formData.photoURL;
      
      // Si hay un archivo de imagen nuevo, lo subimos
      if (imageFile) {
        const storageRef = ref(storage, `levapan/profile_images/${auth.currentUser.uid}`);
        const snapshot = await uploadBytes(storageRef, imageFile);
        photoURL = await getDownloadURL(snapshot.ref);
      }

      // Actualizar en Firebase Auth
      await updateProfile(auth.currentUser, {
        displayName: formData.displayName,
        photoURL: photoURL
      });

      // Comprobar si existe el documento del usuario en Firestore
      const userRef = doc(db, 'levapan_usuarios', auth.currentUser.uid);
      const docSnap = await getDoc(userRef);
      
      // Preparar los datos para Firestore
      const userData = {
        nombre_cliente: formData.displayName,
        telefono_cliente: formData.telefono,
        direccion_cliente: formData.direccion,
        departamento: formData.departamento,
        photo_url: photoURL,
        status_cliente: formData.status_cliente,
        empresa: appConfig.companyCode,
        updated_at: new Date(),
      };

      // Si existe, actualizamos; si no, creamos el documento
      if (docSnap.exists()) {
        await updateDoc(userRef, userData);
      } else {
        // Añadimos campos adicionales para nuevos usuarios
        userData.email = auth.currentUser.email;
        userData.created_at = new Date();
        userData.uid = auth.currentUser.uid;
        
        // Crear el documento
        await setDoc(userRef, userData);
      }

      setSuccess("Perfil actualizado con éxito");
      
      // Actualizar el estado del formulario con la nueva URL de la foto
      setFormData(prev => ({
        ...prev,
        photoURL: photoURL
      }));
      
      setImageFile(null);
    } catch (error) {
      console.error("Error al actualizar perfil:", error);
      setError("No se pudo actualizar tu perfil. Intenta nuevamente.");
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.match('image.*')) {
        setError("Solo se permiten archivos de imagen");
        return;
      }
      
      // Check file size (max 2MB)
      const fileSize = file.size / 1024 / 1024; // in MB
      if (fileSize > 2) {
        setError("La imagen no debe ser mayor a 2MB");
        return;
      }
      
      setImageFile(file);
      
      // Create preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photoURL: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="perfil-container">
      <NavBar />
      <div className="perfil-content">
        <h1 className="page-title">Mi Perfil</h1>
        
        {loading ? (
          <div className="loading-container">
            <div className="spinner">
              <div className="double-bounce1"></div>
              <div className="double-bounce2"></div>
            </div>
            <p>Cargando información...</p>
          </div>
        ) : (
          <div className="perfil-card">
            {success && (
              <div className="success-message">
                <i className="fas fa-check-circle"></i>
                <span>Perfil actualizado correctamente</span>
              </div>
            )}
            
            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}
            
            <form className="perfil-form" onSubmit={handleUpdate}>
              <div className="form-group">
                <label htmlFor="nombre">Nombre completo *</label>
                <div className="input-with-icon">
                  <i className="fas fa-user"></i>
                  <input
                    type="text"
                    id="nombre"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    placeholder="Ingresa tu nombre completo"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-with-icon">
                  <i className="fas fa-envelope"></i>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    disabled
                    className="input-disabled"
                  />
                </div>
                <small className="input-help">El email no se puede cambiar</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="telefono">Teléfono</label>
                <div className="input-with-icon">
                  <i className="fas fa-phone"></i>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="Ingresa tu número de teléfono"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="direccion">Dirección</label>
                <div className="input-with-icon">
                  <i className="fas fa-home"></i>
                  <input
                    type="text"
                    id="direccion"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    placeholder="Ingresa tu dirección"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="departamento">Departamento</label>
                <div className="input-with-icon">
                  <i className="fas fa-building"></i>
                  <input
                    type="text"
                    id="departamento"
                    name="departamento"
                    value={formData.departamento}
                    onChange={handleChange}
                    placeholder="Ingresa tu departamento"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="photoURL">Foto de perfil</label>
                <input
                  type="file"
                  id="photoURL"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
              
              <button
                type="submit"
                className="save-btn"
                disabled={updating}
              >
                {updating ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Guardando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i> Guardar Cambios
                  </>
                )}
              </button>
            </form>
            
            {/* Sección para mostrar otros usuarios del mismo departamento */}
            {userDepartment && (
              <div className="otros-usuarios-seccion">
                <h2>Usuarios de tu departamento</h2>
                {otherUsers.length > 0 ? (
                  <div className="usuarios-lista">
                    {otherUsers.map(user => (
                      <div key={user.id} className="usuario-card">
                        <div className="usuario-avatar">
                          {user.photo_url ? (
                            <img src={user.photo_url} alt={user.nombre_cliente || 'Usuario'} />
                          ) : (
                            <div className="avatar-placeholder">
                              {(user.nombre_cliente || 'U')[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="usuario-info">
                          <h3>{user.nombre_cliente || 'Usuario sin nombre'}</h3>
                          <p>{user.telefono_cliente || 'Sin teléfono'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No hay otros usuarios en tu departamento.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Perfil;
